import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Paquete de servicio completo";
const h1Text = "Su socio estratégico y operativo para un paquete integral full-service en la región DACH. Como su brazo extendido, asumimos la totalidad de su negocio local para que pueda mantener su enfoque en sus mercados de origen.";
const heroImage = "https://net-basis.net/Bilder/ict-full-service-business-development-management.webp";
const heroAlt = "Un empresario usa un panel digital para una oferta TIC integral con gestión centralizada.";
const heroClaimLine1 = "¡Todo de una";
const heroClaimLine2 = "sola fuente!";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __paquete de servicio completo__:";

const p1Text = "NetBasis International trabaja para empresas extranjeras como un pilar local en sus nuevos mercados. A partir de una amplia cartera de servicios, nuestros socios fabricantes pueden seleccionar servicios individuales adaptados a los requisitos específicos de su empresa.";
const p2Text = "El enfoque más integral para ingresar a un nuevo mercado, y por lo tanto la mejor manera de asegurar un trabajo exitoso, es nuestro paquete full-service. De esta manera, nuestros socios fabricantes mantienen la mente despejada para el negocio en sus mercados de origen y confían a NetBasis International, naturalmente en estrecha cooperación, la gestión completa de los nuevos mercados.";
const p3Text = "Con un contrato full-service, NetBasis International asume la totalidad del negocio local, así como la planificación, ejecución y supervisión de todas las tareas requeridas.";

const p1Parts = p1Text.split(COMPANY_NAME);
const p2Parts = p2Text.split(COMPANY_NAME);
const p3Parts = p3Text.split(COMPANY_NAME);

const sections = [
  {
    title: "1. Todo incluido",
    items: [
      "Dirección postal y servicio de oficina completo sobre el terreno",
      "Evaluación técnica y comercial de sus productos",
      "Creación de todos los documentos comerciales y técnicos necesarios",
      "Análisis competitivo intensivo y fundamentado en el mercado objetivo",
      "Fijación de precios estratégica y posicionamiento en el mercado",
      "Comercialización de productos y soluciones seleccionados o en su totalidad como su importador",
      "Registro o notificación oficial de productos de hardware ante las autoridades (como la Stiftung EAR)",
      "Soporte técnico fiable según niveles de servicio claramente definidos",
      "El equipo de NetBasis International desarrolla su negocio como su representación local",
    ],
  },
  {
    title: "2. Práctico",
    items: [
      "Un punto de contacto central para sus inquietudes y para sus clientes locales en inglés y alemán",
      "Ventas completas, marketing dirigido y soporte en su nombre directamente en el mercado",
    ],
  },
  {
    title: "3. Rápido",
    items: [
      "Sin largas fases de inicio: con nosotros estará inmediatamente operativo y \"ready for action\"",
      "Enfoque directo en el trabajo real y productivo sobre el terreno",
      "Sin demoras al contactar a clientes potenciales o socios de distribución cualificados",
      "Soporte local sencillo para clientes, visitas a clientes y participación profesional en ferias",
    ],
  },
  {
    title: "4. Cerca del cliente",
    items: [
      "Presente sobre el terreno sin barreras lingüísticas ni culturales",
      "Imagen pública profesional, experimentada, fiable y orientada al cliente",
      "Alta agilidad empresarial y tiempos de respuesta extremadamente cortos",
    ],
  },
  {
    title: "5. Rentable",
    items: [
      "Sin costes de una oficina internacional costosa y personal: costes fijos mínimos sin grandes inversiones",
      "Tarifas planas mensuales claras y sostenibles más comisión por éxito según el acuerdo de asociación",
    ],
  },
];

const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

export default function ServicioCompletoEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Paquete de Servicio Completo | NetBasis International"
        description="Desde el análisis de mercado hasta las ventas: Nuestro paquete modular completo para el éxito llave en mano de su expansión internacional."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-end pt-[12%] md:pt-[4%] lg:pt-[4%] pr-[8%] md:pr-[4%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5] animate-[presenceTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="hidden md:inline"> </span>}
                  {heroClaimLine2 && <span className="block md:inline">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Sections with green sub-headings and bullet lists */}
            <div className="space-y-5 text-foreground-950 leading-relaxed text-sm md:text-base">
              {sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-primary-600 font-bold text-sm md:text-base mb-2">
                    {section.title}
                  </h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {section.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}